import { preventBubblingEvent } from '@mote/helpers/browser'
import { KeyboardKey } from '@mote/helpers/constants'
import { Block, Button, H3 } from '@mote/ui'
import React from 'react'
import styled, { css } from 'styled-components'
import { ToggleRecordReplySidebarButton } from '../../../recordReplySidebar'
import { CloseButton } from '../../../shared/components'
import { useMotePlayer } from '../../../shared/contexts/motePlayer.context'
import { moteCardFluidDimensionsAdjuster } from '../fluidSizing'
import { MoteCardRichEmojiPicker } from '../MoteCardRichEmojiPicker'
import { getStringWidth } from '@mote/helpers/dom'

export function MoteCardEndState({
  createdByUserId,
  shouldEnableReactions,
  toggleInitialState,
  enableReplyButton,
  onEmojiSelected,
  myReactions,
  replyMotes = [],
  createdByGreeting = ''
}) {
  const { replay, playerDimensions } = useMotePlayer()
  const { replyToText, replyToWidth } = React.useMemo(() => {
    if (!enableReplyButton) return { replyToText: null, replyToWidth: 182 }

    let text = `Reply to ${createdByGreeting}`

    if (replyMotes.length > 0) {
      text += ` (${replyMotes.length})`
    }

    const width = getStringWidth(text, {
      fontSize: 15,
      lineHeight: '120%',
      padding: 16
    })

    return { replyToText: text, replyToWidth: width > 248 ? 248 : width }
  }, [enableReplyButton, createdByGreeting, replyMotes])

  return (
    <Root>
      <StyledCloseButton
        size={moteCardFluidDimensionsAdjuster(36, 32, playerDimensions?.width)}
        onClick={toggleInitialState}
      />
      {shouldEnableReactions && (
        <Block align='center'>
          <H3
            fontSize={moteCardFluidDimensionsAdjuster(
              18,
              12,
              playerDimensions?.width
            )}
            lineHeight='140%'
            color='white'
          >
            Quick react
          </H3>

          <MoteCardRichEmojiPicker
            createdByUserId={createdByUserId}
            onEmojiSelected={onEmojiSelected}
            myReactions={myReactions}
          />
        </Block>
      )}
      <Block
        margin={`${moteCardFluidDimensionsAdjuster(
          28,
          12,
          playerDimensions?.width
        )}px 0 0 0`}
        gap={moteCardFluidDimensionsAdjuster(8, 8, playerDimensions?.width)}
        spreadX
        maxWidth={moteCardFluidDimensionsAdjuster(
          replyToWidth,
          182,
          playerDimensions?.width
        )}
      >
        {enableReplyButton && (
          <ReplyButton
            height={moteCardFluidDimensionsAdjuster(
              40,
              40,
              playerDimensions?.width
            )}
            fontSize={moteCardFluidDimensionsAdjuster(
              15,
              15,
              playerDimensions?.width
            )}
            text={replyToText}
          />
        )}
        <ReplayButton
          ref={btn => btn?.focus()}
          enableReplyButton={enableReplyButton}
          aria-label='Replay'
          text='Play again'
          variant={enableReplyButton ? 'secondary' : 'primary'}
          onClick={event => {
            preventBubblingEvent(event)
            replay()
          }}
          onKeyDown={event => {
            switch (event.keyCode) {
              case KeyboardKey.Space:
              case KeyboardKey.Enter:
                preventBubblingEvent(event)
                replay()
                break
            }
          }}
          height={moteCardFluidDimensionsAdjuster(
            40,
            40,
            playerDimensions?.width
          )}
          fontSize={moteCardFluidDimensionsAdjuster(
            15,
            15,
            playerDimensions?.width
          )}
          block
        />
      </Block>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: rgba(33, 34, 40, 0.4);
  border-radius: 8px;
`

const CloseIconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 6%;
  cursor: pointer;
`

const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  top: 3.5%;
  right: 3.5%;
  color: #ffffff;

  svg {
    height: 24px;
    width: 24px;
  }

  &:hover,
  &:focus {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.2);
  }

  &:focus {
    box-shadow: 0 0 0 2px rgba(47, 166, 255, 0.6);
  }
`

const ReplayButton = styled(Button)`
  &:focus {
    box-shadow: 0 0 0 2px rgba(47, 166, 255, 0.6);
  }

  height: ${({ height }) => height}px;

  ${({ enableReplyButton }) =>
    enableReplyButton &&
    css`
      &:hover {
        background: white;
        opacity: 0.85;
        color: black;
      }
    `};

  ${({ fontSize }) => css`
    span.button-text {
      font-size: ${fontSize}px;
    }
  `}
`

const ReplyButton = styled(ToggleRecordReplySidebarButton)`
  &:focus {
    box-shadow: 0 0 0 2px rgba(47, 166, 255, 0.6);
  }

  height: ${({ height }) => height}px;

  ${({ fontSize }) => css`
    span.button-text {
      font-size: ${fontSize}px;
    }
  `}
`
